// ─────────────────────────────────────────────────────────────────────────────
// usePropFirmPresets
//
// Loads the list of prop firm presets from Supabase via the same REST
// pattern the rest of the app uses (see lib/supabase.js + hooks/useSupabase.js).
// No supabase-js client dependency — just authFetch (which wraps
// ensureValidToken + authHeaders + fetch so token refresh is handled
// automatically before each request).
//
// Presets rarely change, so we cache them at module level.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { SUPABASE_URL, authFetch } from "../lib/supabase";

let cachedPresets = null;
let inflightPromise = null;

async function fetchPresets() {
  if (cachedPresets) return cachedPresets;
  if (inflightPromise) return inflightPromise;

  if (!SUPABASE_URL) {
    cachedPresets = [];
    return cachedPresets;
  }

  inflightPromise = (async () => {
    try {
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/prop_firm_presets?is_active=eq.true&order=display_order.asc&select=*`
      );
      if (!res.ok) {
        console.error("Failed to load prop firm presets:", res.status, await res.text());
        cachedPresets = [];
        return cachedPresets;
      }
      const data = await res.json();
      cachedPresets = Array.isArray(data) ? data : [];
      return cachedPresets;
    } catch (e) {
      console.error("usePropFirmPresets fetch error:", e);
      cachedPresets = [];
      return cachedPresets;
    } finally {
      inflightPromise = null;
    }
  })();

  return inflightPromise;
}

export function usePropFirmPresets() {
  const [presets, setPresets] = useState(cachedPresets || []);
  const [loading, setLoading] = useState(!cachedPresets);

  useEffect(() => {
    let cancelled = false;
    fetchPresets().then(data => {
      if (!cancelled) {
        setPresets(data);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  return { presets, loading };
}
